/**
 * useSupersedeBatchConfirm (2026-07-08)
 *
 * Shared gate for the coordinator batch-create flow. When a re-batch would
 * invalidate already-printed-and-stuck stickers, the backend throws CONFLICT
 * `SUPERSEDE_CONFIRM_REQUIRED:<count>` (unless `confirmSupersede` was set). This
 * hook centralises the "N shirts already have stickers" confirm dialog + the
 * confirmed retry so every createBatch call site (OrdersPage manual/auto,
 * ManualBatchMode, AutoBatchMode) stays DRY.
 *
 * Usage — the call site keeps ALL of its own success/error handling in ONE
 * function that takes a `confirmSupersede` flag, and hands the gate its error +
 * a retry:
 *
 *   const supersede = useSupersedeBatchConfirm();
 *   const create = async (confirmSupersede = false) => {
 *     try {
 *       const r = await mutateAsync({ ...input, confirmSupersede });
 *       // success toast / navigate
 *     } catch (error) {
 *       if (!confirmSupersede && supersede.intercept(error, () => create(true))) return;
 *       toast.error(...);
 *     }
 *   };
 *   // <SupersedeBatchConfirmDialog {...supersede.dialogProps} />
 */
import { useCallback, useRef, useState } from 'react';
import { parseSupersedeConfirmError } from '@/lib/trpc-error-utils';

export function useSupersedeBatchConfirm() {
  const [pending, setPending] = useState<{ count: number } | null>(null);
  const retryRef = useRef<(() => void) | null>(null);

  /**
   * Returns true (and opens the confirm dialog) when `error` is a
   * SUPERSEDE_CONFIRM_REQUIRED conflict — stashing `retry` for the confirmed
   * re-run. Returns false for any other error so the caller handles it normally.
   */
  const intercept = useCallback((error: unknown, retry: () => void): boolean => {
    const s = parseSupersedeConfirmError(error);
    if (!s.needsConfirm) return false;
    retryRef.current = retry;
    setPending({ count: s.count });
    return true;
  }, []);

  const confirm = useCallback(() => {
    const retry = retryRef.current;
    retryRef.current = null;
    setPending(null);
    retry?.();
  }, []);

  const cancel = useCallback(() => {
    retryRef.current = null;
    setPending(null);
  }, []);

  return {
    supersedeCount: pending?.count ?? 0,
    isConfirmOpen: !!pending,
    intercept,
    confirm,
    cancel,
    /** Spread onto <SupersedeBatchConfirmDialog>. */
    dialogProps: {
      open: !!pending,
      count: pending?.count ?? 0,
      onConfirm: confirm,
      onCancel: cancel,
    },
  };
}
